import React from 'react';
import { cn } from './utils';

export const Input = React.forwardRef(({ label, hint, error, className = '', id, ...rest }, ref) => {
  const uid = id || `input-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={uid} className="k-small font-semibold text-[var(--ink)]">
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={uid}
        className={cn(
          'h-11 px-4 rounded-md bg-[var(--surface)] text-[var(--ink)] placeholder:text-[var(--muted)]',
          'border border-[var(--line)] focus:outline-none focus:ring-0',
          'focus-visible:shadow-spectrum-ring',
          error && 'border-sev-high',
          className,
        )}
        {...rest}
      />
      {hint && !error && <span className="k-small text-[var(--muted)]">{hint}</span>}
      {error && <span className="k-small text-sev-high">{error}</span>}
    </div>
  );
});
Input.displayName = 'Input';

export const Textarea = React.forwardRef(({ label, hint, error, className = '', id, rows = 4, ...rest }, ref) => {
  const uid = id || `ta-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <div className="flex flex-col gap-1">
      {label && <label htmlFor={uid} className="k-small font-semibold text-[var(--ink)]">{label}</label>}
      <textarea
        ref={ref}
        id={uid}
        rows={rows}
        className={cn(
          'px-4 py-3 rounded-md bg-[var(--surface)] text-[var(--ink)] placeholder:text-[var(--muted)]',
          'border border-[var(--line)] focus:outline-none focus-visible:shadow-spectrum-ring',
          error && 'border-sev-high',
          className,
        )}
        {...rest}
      />
      {hint && !error && <span className="k-small text-[var(--muted)]">{hint}</span>}
      {error && <span className="k-small text-sev-high">{error}</span>}
    </div>
  );
});
Textarea.displayName = 'Textarea';

export const Select = React.forwardRef(({ label, hint, error, options = [], placeholder, className = '', id, ...rest }, ref) => {
  const uid = id || `sel-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <div className="flex flex-col gap-1">
      {label && <label htmlFor={uid} className="k-small font-semibold text-[var(--ink)]">{label}</label>}
      <select
        ref={ref}
        id={uid}
        className={cn(
          'h-11 px-3 rounded-md bg-[var(--surface)] text-[var(--ink)]',
          'border border-[var(--line)] focus:outline-none focus-visible:shadow-spectrum-ring',
          error && 'border-sev-high',
          className,
        )}
        {...rest}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {hint && !error && <span className="k-small text-[var(--muted)]">{hint}</span>}
      {error && <span className="k-small text-sev-high">{error}</span>}
    </div>
  );
});
Select.displayName = 'Select';

export const Checkbox = React.forwardRef(({ label, className = '', id, ...rest }, ref) => {
  const uid = id || `cb-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <label htmlFor={uid} className="inline-flex items-center gap-3 cursor-pointer select-none">
      <input
        ref={ref}
        id={uid}
        type="checkbox"
        className={cn(
          'h-5 w-5 rounded-sm appearance-none border-2 border-[var(--line)] bg-[var(--surface)]',
          'checked:bg-spectrum checked:border-transparent transition-all',
          'focus-visible:shadow-spectrum-ring relative',
          className,
        )}
        {...rest}
      />
      {label && <span className="k-body text-[var(--ink)]">{label}</span>}
    </label>
  );
});
Checkbox.displayName = 'Checkbox';
